/**
 * Estado do wizard, mantido só em memória.
 *
 * O spec §1 proíbe salvamento parcial: um refresh reinicia no Step 0. Por isso
 * não há localStorage nem query cache aqui — só `useState` no componente raiz,
 * exposto por contexto.
 *
 * `data` é a resposta do `resolve` e nunca é alterada: o front apenas renderiza
 * o JSON que o backend entrega. O que muda é `state`, que é exatamente o
 * material do submit.
 */

import { createContext, useContext } from "react";

import {
  ExtensionOutcome,
  ExtensionStep,
  IExtensionFormState,
  IResolveResponse,
} from "./types";

/** Extremos do wizard, usados no clamp da navegação. */
const FIRST_STEP = ExtensionStep.Login;

const LAST_STEP = ExtensionStep.Review;

export interface IExtensionContextValue {
  /** Token da URL, repassado a cada chamada de API. */
  token: string;
  /** Resposta do `resolve`, imutável. */
  data: IResolveResponse;
  state: IExtensionFormState;
  currentStep: ExtensionStep;
  /** Aplica um patch imutável sobre o estado. */
  updateState: (patch: Partial<IExtensionFormState>) => void;
  goToStep: (step: ExtensionStep) => void;
  goNext: () => void;
  goBack: () => void;
  /** Troca o wizard por uma das telas terminais. */
  setOutcome: (outcome: ExtensionOutcome) => void;
}

export const ExtensionContext = createContext<IExtensionContextValue | null>(
  null
);

export function useExtensionForm(): IExtensionContextValue {
  const context = useContext(ExtensionContext);

  if (!context) {
    throw new Error(
      "useExtensionForm precisa estar dentro de <ExtensionContext.Provider>."
    );
  }

  return context;
}

/**
 * Estado inicial. Todos os dependentes entram marcados (spec §7) e o contato
 * começa vazio para o titular confirmar ativamente — o `resolve` não devolve
 * e-mail/telefone atuais no contrato da Lane 5.
 */
export function createInitialState(): IExtensionFormState {
  return {
    taxPayerId: "",
    hasAcceptedLgpd: false,
    offerDecision: null,
    contact: { personalEmail: "", mobilePhone: "" },
    retainedDependentTaxPayerIds: [],
    dependentEdits: {},
    chosenPlanId: null,
    uploadedDocuments: [],
    hasAcceptedRn561: false,
  };
}

/**
 * Estado inicial já ajustado ao que o `resolve` devolveu.
 *
 * `createInitialState()` não conhece os dependentes e por isso devolve a lista
 * de retenção vazia; aqui ela é semeada com TODOS os CPFs recebidos, porque o
 * spec §7 manda que os dependentes entrem marcados e o titular apenas desmarque
 * quem não quiser manter. O aceite LGPD volta pré-marcado quando o candidato já
 * aceitou antes (spec §10.6).
 */
export function createSeededState(
  data: IResolveResponse
): IExtensionFormState {
  return {
    ...createInitialState(),
    /**
     * "Sim, quero manter" entra pré-selecionado: é o caminho que a oferta
     * propõe, e deixar os dois botões neutros obrigava um clique sem
     * necessidade. Recusar continua exigindo ação explícita mais o modal.
     */
    offerDecision: "KEEP",
    hasAcceptedLgpd: data.hasPriorLgpdAcceptance,
    retainedDependentTaxPayerIds: data.dependents.map(
      (dependent) => dependent.taxPayerId
    ),
  };
}

/**
 * Mantém a navegação dentro dos passos existentes: avançar no último passo ou
 * voltar no primeiro não sai do intervalo.
 */
export function clampStep(step: number): ExtensionStep {
  if (step < FIRST_STEP) return FIRST_STEP;
  if (step > LAST_STEP) return LAST_STEP;

  return step as ExtensionStep;
}
